import { useState } from 'react'
import { surveyBlocks } from '../data/surveyQuestions'
import { generateDiagnostic } from '../data/recommendations'
import { SurveyAnswers, DiagnosticResult } from '../types/survey'
import { submitToGoogleSheets } from '../services/googleSheets'

export function useSurvey() {
  const [hasConsented, setHasConsented] = useState(false)
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  const [answers, setAnswers] = useState<SurveyAnswers>({})
  const [isFinished, setIsFinished] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [result, setResult] = useState<DiagnosticResult | null>(null)

  const currentBlock = surveyBlocks[currentBlockIndex]
  const isLastBlock = currentBlockIndex === surveyBlocks.length - 1
  const totalSteps = surveyBlocks.length

  const handleConsent = () => {
    setHasConsented(true)
    window.scrollTo(0, 0)
  }

  const handleAnswer = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }))
  }

  const canProceed = () => {
    if (!currentBlock) return false

    // Check if all visible required questions in the current block are answered
    const visibleQuestions = currentBlock.questions.filter((q) => {
      // If it's conditional, check if condition is met
      if (q.conditionalOn) {
        const { questionId, expectedValue } = q.conditionalOn
        return answers[questionId] === expectedValue
      }
      return true
    })

    return visibleQuestions.every((q) => !!answers[q.id])
  }

  const nextBlock = () => {
    if (isLastBlock) {
      finishSurvey()
    } else {
      setCurrentBlockIndex((prev) => prev + 1)
      window.scrollTo(0, 0)
    }
  }

  const prevBlock = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex((prev) => prev - 1)
      window.scrollTo(0, 0)
    }
  }

  const finishSurvey = async () => {
    const diagnostic = generateDiagnostic(answers)
    setResult(diagnostic)
    setIsFinished(true)
    setIsSubmitting(true)
    setSubmitError(false)
    window.scrollTo(0, 0)

    // Enviar dados para o Google Sheets em background
    try {
      await submitToGoogleSheets({
        answers,
        result: diagnostic,
      })
    } catch (error) {
      console.error('Erro ao salvar na planilha:', error)
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetSurvey = () => {
    setHasConsented(false)
    setCurrentBlockIndex(0)
    setAnswers({})
    setIsFinished(false)
    setIsSubmitting(false)
    setSubmitError(false)
    setResult(null)
    window.scrollTo(0, 0)
  }

  return {
    hasConsented,
    currentBlock,
    currentBlockIndex,
    totalSteps,
    answers,
    isFinished,
    isSubmitting,
    submitError,
    result,
    canProceed: canProceed(),
    handleConsent,
    handleAnswer,
    nextBlock,
    prevBlock,
    resetSurvey,
  }
}
